import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { getMoldById, updateMold, checkDuplicateMoldNumber } from '../services/mold.service';
import { customerService } from '../services/customer.service';
import { locationService } from '../services/location.service';
import { getProjects } from '../services/project.service';
import storageService from '../services/storage.service';
import { Customer, Location, Mold, MoldStatus, Project, CavityInfo } from '../types';

const MoldEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);

  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [existingThumbnails, setExistingThumbnails] = useState<string[]>([]);
  const [existing3DFile, setExisting3DFile] = useState<Mold['file3D']>(undefined);
  const [newImageFiles, setNewImageFiles] = useState<File[]>([]);
  const [newImagePreviews, setNewImagePreviews] = useState<string[]>([]);
  const [new3DFile, setNew3DFile] = useState<File | null>(null);
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);
  const [delete3DFile, setDelete3DFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const [formData, setFormData] = useState({
    moldNumber: '',
    moldName: '',
    customerId: '',
    projectId: '',
    productName: '',
    cavityCount: 1,
    cavity: '',
    designLife: 100000,
    locationId: '',
    status: 'stored' as MoldStatus,
    // v2.0 추가 필드
    productNumber: '',
    productionStartDate: '',
    disposalDate: '',
    assetType: 'owned' as 'owned' | 'leased' | 'consigned',
    manufacturer: '',
    moldType: '' as '' | 'injection-trial' | 'injection-production' | 'press' | 'die-casting' | 'forging' | 'other',
    resinType: '',
    managerId: '',
    managerName: '',
    weight: '',
    dimensions: {
      width: '',
      length: '',
      height: '',
    },
    notes: '',
  });

  const [cavities, setCavities] = useState<CavityInfo[]>([]);

  // 금형번호 중복 체크 상태
  const [originalMoldNumber, setOriginalMoldNumber] = useState('');
  const [moldNumberChecking, setMoldNumberChecking] = useState(false);
  const [moldNumberDuplicate, setMoldNumberDuplicate] = useState<boolean | null>(null);

  useEffect(() => {
    const loadMold = async () => {
      if (!id) {
        setError('금형 ID가 올바르지 않습니다.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const [mold, customerData, locationData, projectData] = await Promise.all([
          getMoldById(id),
          customerService.getAll(),
          locationService.getAll(),
          getProjects(),
        ]);

        setCustomers(customerData);
        setLocations(locationData);
        setProjects(projectData);

        if (mold) {
          setFormData({
            moldNumber: mold.moldNumber,
            moldName: mold.moldName,
            customerId: mold.customerId || '',
            projectId: mold.projectId || '',
            productName: mold.productName,
            cavityCount: mold.cavityCount,
            cavity: mold.cavity || '',
            designLife: mold.designLife,
            locationId: mold.locationId || '',
            status: (mold.status as MoldStatus) || 'stored',
            // v2.0 필드
            productNumber: mold.productNumber || '',
            productionStartDate: mold.productionStartDate || '',
            disposalDate: mold.disposalDate || '',
            assetType: mold.assetType || 'owned',
            manufacturer: mold.manufacturer || '',
            moldType: mold.moldType || '',
            resinType: mold.resinType || '',
            managerId: mold.managerId || '',
            managerName: mold.managerName || '',
            weight: mold.weight?.toString() || '',
            dimensions: {
              width: mold.dimensions?.width?.toString() || '',
              length: mold.dimensions?.length?.toString() || '',
              height: mold.dimensions?.height?.toString() || '',
            },
            notes: mold.notes || '',
          });

          // cavities 초기화
          if (mold.cavities && Array.isArray(mold.cavities)) {
            setCavities(mold.cavities);
          }

          // 원래 금형번호 저장
          setOriginalMoldNumber(mold.moldNumber);

          if (mold.images) {
            setExistingImages(mold.images);
          }
          if (mold.thumbnails) {
            setExistingThumbnails(mold.thumbnails);
          }
          if (mold.file3D) {
            setExisting3DFile(mold.file3D);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : '금형 정보를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };

    loadMold();
  }, [id]);

  // 금형번호 중복 체크 (실시간, 자기 자신 제외)
  useEffect(() => {
    const checkDuplicate = async () => {
      // 원래 번호와 같으면 체크 안 함
      if (!formData.moldNumber || formData.moldNumber === originalMoldNumber) {
        setMoldNumberDuplicate(null);
        return;
      }

      if (formData.moldNumber.length < 2) {
        setMoldNumberDuplicate(null);
        return;
      }

      setMoldNumberChecking(true);
      try {
        const isDuplicate = await checkDuplicateMoldNumber(formData.moldNumber, id);
        setMoldNumberDuplicate(isDuplicate);
      } catch (err) {
        console.error('중복 체크 실패:', err);
        setMoldNumberDuplicate(null);
      } finally {
        setMoldNumberChecking(false);
      }
    };

    const timer = setTimeout(checkDuplicate, 800);
    return () => clearTimeout(timer);
  }, [formData.moldNumber, originalMoldNumber, id]);

  const handleInputChange =
    (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        field === 'cavityCount' || field === 'designLife'
          ? parseInt(event.target.value, 10) || 0
          : event.target.value;

      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  // 패밀리 수 변경 시 cavities 배열 업데이트
  const handleCavityCountChange = (count: number) => {
    setFormData((prev) => ({ ...prev, cavityCount: count }));
    
    const newCavities = Array.from({ length: count }, (_, i) => ({
      cavityNumber: i + 1,
      productName: formData.productName || '',
    }));
    setCavities(newCavities);
  };

  // 개별 패밀리 제품명 변경 핸들러
  const handleCavityProductNameChange = (cavityNumber: number, productName: string) => {
    setCavities((prev) =>
      prev.map((c) => (c.cavityNumber === cavityNumber ? { ...c, productName } : c))
    );
  };

  const handleDimensionChange =
    (dimension: 'width' | 'length' | 'height') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        dimensions: {
          ...prev.dimensions,
          [dimension]: event.target.value,
        },
      }));
    };

  const handleExistingImageRemove = (imageUrl: string) => {
    const index = existingImages.indexOf(imageUrl);
    setExistingImages((prev) => prev.filter((url) => url !== imageUrl));
    if (index >= 0 && index < existingThumbnails.length) {
      const thumbnailUrl = existingThumbnails[index];
      setExistingThumbnails((prev) => prev.filter((_, i) => i !== index));
      setImagesToDelete((prev) => [...prev, imageUrl, thumbnailUrl]);
    } else {
      setImagesToDelete((prev) => [...prev, imageUrl]);
    }
  };

  const handleNewImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    const totalImages = existingImages.length + newImageFiles.length + newFiles.length;

    if (totalImages > 4) {
      setError('이미지는 최대 4장까지 업로드할 수 있습니다.');
      return;
    }

    newFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewImagePreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });

    setNewImageFiles((prev) => [...prev, ...newFiles]);
  };

  const handleNewImageRemove = (index: number) => {
    setNewImageFiles((prev) => prev.filter((_, i) => i !== index));
    setNewImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleExisting3DFileRemove = () => {
    setExisting3DFile(undefined);
    setDelete3DFile(true);
  };

  const handleNew3DFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validExtensions = [
      '3dm', '3ds', '3mf', 'amf', 'bim', 'brep', 
      'dae', 'fbx', 'fcstd', 'gltf', 'glb', 
      'ifc', 'iges', 'igs', 'step', 'stp', 
      'stl', 'obj', 'off', 'ply', 'wrl'
    ];
    const fileExt = file.name.split('.').pop()?.toLowerCase();

    if (!fileExt || !validExtensions.includes(fileExt)) {
      setError('지원하지 않는 3D 파일 형식입니다.');
      return;
    }

    setNew3DFile(file);
    setExisting3DFile(undefined);
    setDelete3DFile(true);
  };

  const handleNew3DFileRemove = () => {
    setNew3DFile(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!id) {
      setError('금형 ID가 올바르지 않습니다.');
      return;
    }

    if (!formData.moldNumber || !formData.moldName || !formData.customerId || !formData.locationId) {
      setError('필수 항목을 모두 입력해 주세요.');
      return;
    }

    // 금형번호 변경 시 중복 체크
    if (formData.moldNumber !== originalMoldNumber) {
      // 이미 중복 체크된 경우
      if (moldNumberDuplicate === true) {
        setError(`금형번호 "${formData.moldNumber}"는 이미 사용 중입니다. 다른 번호를 입력해주세요.`);
        return;
      }

      // 최종 중복 체크 (실시간 체크와 제출 사이 시간 차이 대비)
      const isDuplicate = await checkDuplicateMoldNumber(formData.moldNumber, id);
      if (isDuplicate) {
        setError(`금형번호 "${formData.moldNumber}"는 이미 사용 중입니다. 다른 번호를 입력해주세요.`);
        setMoldNumberDuplicate(true);
        return;
      }
    }

    setSaving(true);
    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      setUploadProgress(20);

      if (imagesToDelete.length > 0) {
        await storageService.deleteFiles(imagesToDelete);
        setUploadProgress(30);
      }

      if (delete3DFile && existing3DFile) {
        await storageService.deleteFile(existing3DFile.url);
        setUploadProgress(40);
      }

      const newImageUrls: string[] = [];
      const newThumbnailUrls: string[] = [];
      if (newImageFiles.length > 0) {
        for (let i = 0; i < newImageFiles.length; i += 1) {
          const { original, thumbnail } = await storageService.uploadImageWithThumbnail(
            newImageFiles[i], 
            id, 
            existingImages.length + i
          );
          newImageUrls.push(original);
          newThumbnailUrls.push(thumbnail);
          setUploadProgress(40 + ((i + 1) / newImageFiles.length) * 30);
        }
      }

      setUploadProgress(70);

      let new3DFileData: Mold['file3D'] = undefined;
      if (new3DFile) {
        new3DFileData = await storageService.upload3DFile(new3DFile, id);
        setUploadProgress(85);
      }

      const finalImages = [...existingImages, ...newImageUrls];
      const finalThumbnails = [...existingThumbnails, ...newThumbnailUrls];
      const final3DFile = new3DFile ? new3DFileData : delete3DFile ? undefined : existing3DFile;

      // v2.0: undefined 제거 및 선택 필드 조건부 포함
      const updateData: any = {
        moldNumber: formData.moldNumber,
        moldName: formData.moldName,
        customerId: formData.customerId,
        productName: formData.productName,
        cavityCount: formData.cavityCount,
        designLife: formData.designLife,
        locationId: formData.locationId,
        status: formData.status,
        ...(formData.projectId && { projectId: formData.projectId }),
        ...(formData.cavity && { cavity: formData.cavity }),
        ...(cavities.length > 0 && { cavities }),
        ...(finalImages.length > 0 && { images: finalImages }),
        ...(finalThumbnails.length > 0 && { thumbnails: finalThumbnails }),
        ...(final3DFile && { file3D: final3DFile }),
        // v2.0 선택 필드
        ...(formData.productNumber && { productNumber: formData.productNumber }),
        ...(formData.productionStartDate && { productionStartDate: formData.productionStartDate }),
        ...(formData.disposalDate && { disposalDate: formData.disposalDate }),
        ...(formData.assetType && { assetType: formData.assetType }),
        ...(formData.manufacturer && { manufacturer: formData.manufacturer }),
        ...(formData.moldType && { moldType: formData.moldType }),
        ...(formData.resinType && { resinType: formData.resinType }),
        ...(formData.managerId && { managerId: formData.managerId }),
        ...(formData.managerName && { managerName: formData.managerName }),
        ...(formData.weight && { weight: parseFloat(formData.weight) }),
        ...((formData.dimensions.width || formData.dimensions.length || formData.dimensions.height) && {
          dimensions: {
            ...(formData.dimensions.width && { width: parseFloat(formData.dimensions.width) }),
            ...(formData.dimensions.length && { length: parseFloat(formData.dimensions.length) }),
            ...(formData.dimensions.height && { height: parseFloat(formData.dimensions.height) }),
          },
        }),
        ...(formData.notes && { notes: formData.notes }),
      };

      await updateMold(id, updateData);

      setUploadProgress(100);
      setSuccess(true);

      setTimeout(() => {
        navigate(`/molds/${id}`);
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : '수정 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
      setIsUploading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="body1" color="text.secondary">
          금형 정보를 불러오는 중...
        </Typography>
      </Box>
    );
  }

  if (success) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: { xs: 2, md: 3 },
        }}
      >
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          금형 정보가 성공적으로 수정되었습니다. 상세 페이지로 이동합니다...
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 3, md: 4 },
        p: { xs: 2, md: 3 },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1.5, md: 2 }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
      >
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/molds/${id}`)}
          sx={{
            borderRadius: 2,
            px: 2.5,
            py: 1,
            fontWeight: 600,
            letterSpacing: '0.04em',
          }}
        >
          상세보기
        </Button>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
            금형 정보 수정
          </Typography>
          <Typography variant="body2" sx={{ color: alpha(theme.palette.text.secondary, 0.78) }}>
            사용 현황과 자산 정보를 최신 상태로 유지해 주세요.
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Card
        sx={{
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
          backgroundImage: `linear-gradient(160deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(
            theme.palette.primary.dark,
            0.26,
          )})`,
          boxShadow: `0 20px 38px ${alpha(theme.palette.primary.dark, 0.28)}`,
          backdropFilter: 'blur(14px)',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: { xs: 2.5, md: 3 },
                }}
              >
                <TextField
                  required
                  fullWidth
                  label="금형 번호"
                  value={formData.moldNumber}
                  onChange={handleInputChange('moldNumber')}
                  disabled={true}
                  error={moldNumberDuplicate === true}
                  helperText={
                    moldNumberChecking
                      ? '중복 확인 중...'
                      : moldNumberDuplicate === true
                      ? '이미 사용 중인 금형번호입니다.'
                      : moldNumberDuplicate === false
                      ? '사용 가능한 금형번호입니다.'
                      : formData.moldNumber === originalMoldNumber
                      ? '원래 금형번호입니다.'
                      : ''
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-error': {
                        '& fieldset': {
                          borderColor: 'error.main',
                        },
                      },
                      ...(moldNumberDuplicate === false && {
                        '& fieldset': {
                          borderColor: 'success.main',
                        },
                      }),
                    },
                    '& .MuiFormHelperText-root': {
                      ...(moldNumberDuplicate === false && {
                        color: 'success.main',
                      }),
                    },
                  }}
                />
                <TextField
                  required
                  fullWidth
                  label="금형명"
                  value={formData.moldName}
                  onChange={handleInputChange('moldName')}
                  disabled={true}
                />
                <TextField
                  required
                  fullWidth
                  select
                  label="고객사"
                  value={formData.customerId}
                  onChange={handleInputChange('customerId')}
                >
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </MenuItem>
                  ))}
                </TextField>

                <Autocomplete
                  fullWidth
                  options={projects}
                  getOptionLabel={(option) => `${option.projectNo} - ${option.projectName}`}
                  value={projects.find(p => p.id === formData.projectId) || null}
                  onChange={(_, newValue) => {
                    setFormData(prev => ({ ...prev, projectId: newValue?.id || '' }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="연결 프로젝트 (선택사항)"
                      placeholder="프로젝트를 선택하세요"
                    />
                  )}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                    },
                  }}
                />

                <TextField
                  required
                  fullWidth
                  label="제품명"
                  value={formData.productName}
                  onChange={handleInputChange('productName')}
                />
                <TextField
                  fullWidth
                  label="캐비티 구성 (선택사항)"
                  value={formData.cavity}
                  onChange={handleInputChange('cavity')}
                  placeholder="예: 1x8, 2x2"
                  helperText="캐비티 배열을 입력하세요 (예: 1x8, 2x2)"
                />
                <TextField
                  required
                  fullWidth
                  label="패밀리 수"
                  type="number"
                  value={formData.cavityCount}
                  onChange={(e) => handleCavityCountChange(Number(e.target.value))}
                  inputProps={{ min: 1 }}
                  helperText="캐비티(패밀리) 개수를 입력하세요"
                />
                <TextField
                  required
                  fullWidth
                  label="설계 수명 (샷)"
                  type="number"
                  value={formData.designLife}
                  onChange={handleInputChange('designLife')}
                  inputProps={{ min: 1000 }}
                />
                <TextField
                  required
                  fullWidth
                  select
                  label="보관 위치"
                  value={formData.locationId}
                  onChange={handleInputChange('locationId')}
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name} ({location.zone})
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  required
                  fullWidth
                  select
                  label="현재 상태"
                  value={formData.status}
                  onChange={handleInputChange('status')}
                >
                  <MenuItem value="stored">보관 중</MenuItem>
                  <MenuItem value="in-use">사용 중</MenuItem>
                  <MenuItem value="maintenance">점검 중</MenuItem>
                  <MenuItem value="disposed">폐기 완료</MenuItem>
                </TextField>
              </Box>

              {/* 패밀리별 제품명 */}
              {cavities.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    패밀리별 제품명
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                      gap: { xs: 2.5, md: 3 },
                    }}
                  >
                    {cavities.map((cavity) => (
                      <TextField
                        key={cavity.cavityNumber}
                        fullWidth
                        label={`패밀리 #${cavity.cavityNumber}`}
                        value={cavity.productName}
                        onChange={(e) => handleCavityProductNameChange(cavity.cavityNumber, e.target.value)}
                        placeholder="제품명을 입력하세요"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* v2.0: 추가 정보 섹션 */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  추가 정보 (선택사항)
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: { xs: 2.5, md: 3 },
                  }}
                >
                  <TextField
                    fullWidth
                    label="제품 번호"
                    value={formData.productNumber}
                    onChange={handleInputChange('productNumber')}
                  />
                  <TextField
                    fullWidth
                    label="양산 시작일"
                    type="date"
                    value={formData.productionStartDate}
                    onChange={handleInputChange('productionStartDate')}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    fullWidth
                    label="폐기 예정일"
                    type="date"
                    value={formData.disposalDate}
                    onChange={handleInputChange('disposalDate')}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    fullWidth
                    select
                    label="자산 구분"
                    value={formData.assetType}
                    onChange={handleInputChange('assetType')}
                  >
                    <MenuItem value="owned">자가</MenuItem>
                    <MenuItem value="leased">임대</MenuItem>
                    <MenuItem value="consigned">위탁</MenuItem>
                  </TextField>
                  <TextField
                    fullWidth
                    label="금형 제작 업체"
                    value={formData.manufacturer}
                    onChange={handleInputChange('manufacturer')}
                  />
                  <TextField
                    fullWidth
                    select
                    label="금형 종류"
                    value={formData.moldType}
                    onChange={handleInputChange('moldType')}
                  >
                    <MenuItem value="">선택 안함</MenuItem>
                    <MenuItem value="injection-trial">사출금형(시금형)</MenuItem>
                    <MenuItem value="injection-production">사출금형(양산금형)</MenuItem>
                    <MenuItem value="press">프레스금형</MenuItem>
                    <MenuItem value="die-casting">다이캐스팅금형</MenuItem>
                    <MenuItem value="forging">단조금형</MenuItem>
                    <MenuItem value="other">기타</MenuItem>
                  </TextField>
                  <TextField
                    fullWidth
                    label="수지 정보"
                    value={formData.resinType}
                    onChange={handleInputChange('resinType')}
                    placeholder="예: ABS, PP, PC 등"
                  />
                  <TextField
                    fullWidth
                    label="관리 담당자 ID"
                    value={formData.managerId}
                    onChange={handleInputChange('managerId')}
                  />
                  <TextField
                    fullWidth
                    label="관리 담당자명"
                    value={formData.managerName}
                    onChange={handleInputChange('managerName')}
                  />
                  <TextField
                    fullWidth
                    label="중량 (kg)"
                    type="number"
                    value={formData.weight}
                    onChange={handleInputChange('weight')}
                    inputProps={{ step: '0.1', min: '0' }}
                  />
                  <TextField
                    fullWidth
                    label="가로 (mm)"
                    type="number"
                    value={formData.dimensions.width}
                    onChange={handleDimensionChange('width')}
                    inputProps={{ step: '0.1', min: '0' }}
                  />
                  <TextField
                    fullWidth
                    label="세로 (mm)"
                    type="number"
                    value={formData.dimensions.length}
                    onChange={handleDimensionChange('length')}
                    inputProps={{ step: '0.1', min: '0' }}
                  />
                  <TextField
                    fullWidth
                    label="높이 (mm)"
                    type="number"
                    value={formData.dimensions.height}
                    onChange={handleDimensionChange('height')}
                    inputProps={{ step: '0.1', min: '0' }}
                  />
                  <TextField
                    fullWidth
                    label="비고"
                    value={formData.notes}
                    onChange={handleInputChange('notes')}
                    multiline
                    rows={3}
                    sx={{ gridColumn: { sm: 'span 2' } }}
                  />
                </Box>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  파일 관리
                </Typography>
                <Box sx={{ gridColumn: { sm: 'span 2' }, mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                    <ImageIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    등록된 이미지
                  </Typography>
                  {existingImages.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      등록된 이미지가 없습니다.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {existingImages.map((imageUrl) => (
                        <Box
                          key={imageUrl}
                          sx={{
                            position: 'relative',
                            width: 120,
                            height: 120,
                            border: `1px solid ${alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.18 : 0.12)}`,
                            borderRadius: 2,
                            overflow: 'hidden',
                            backgroundColor: alpha(theme.palette.primary.dark, 0.24),
                            boxShadow: `0 12px 24px ${alpha(theme.palette.primary.dark, 0.28)}`,
                          }}
                        >
                          <img
                            src={imageUrl}
                            alt="mold"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleExistingImageRemove(imageUrl)}
                            disabled={isUploading}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              bgcolor: alpha(theme.palette.common.black, 0.5),
                              color: theme.palette.common.white,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.common.black, 0.7),
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    disabled={isUploading}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      borderColor: alpha(theme.palette.primary.light, 0.38),
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: alpha(theme.palette.primary.light, 0.6),
                        backgroundColor: alpha(theme.palette.primary.light, 0.12),
                      },
                    }}
                  >
                    새 이미지 추가
                    <input type="file" hidden accept="image/*" multiple onChange={handleNewImageSelect} />
                  </Button>

                  {newImagePreviews.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {newImagePreviews.map((preview, index) => (
                        <Box
                          key={preview}
                          sx={{
                            position: 'relative',
                            width: 120,
                            height: 120,
                            border: `1px solid ${alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.18 : 0.12)}`,
                            borderRadius: 2,
                            overflow: 'hidden',
                            backgroundColor: alpha(theme.palette.primary.dark, 0.24),
                            boxShadow: `0 12px 24px ${alpha(theme.palette.primary.dark, 0.28)}`,
                          }}
                        >
                          <img
                            src={preview}
                            alt={`new-preview-${index}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleNewImageRemove(index)}
                            disabled={isUploading}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              bgcolor: alpha(theme.palette.common.black, 0.5),
                              color: theme.palette.common.white,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.common.black, 0.7),
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                <Box sx={{ gridColumn: { sm: 'span 2' }, mt: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    <AttachFileIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    3D 파일 관리 (STL, OBJ, STEP, IGES)
                  </Typography>

                  {existing3DFile && !delete3DFile && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        mb: 2,
                        border: `1px solid ${alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.18 : 0.12)}`,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.primary.dark, 0.2),
                      }}
                    >
                      <Box sx={{ overflow: 'hidden', width: '100%', flex: 1 }}>
                        <Typography variant="body1" fontWeight="bold" sx={{
                          maxWidth: '100%',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          hyphens: 'auto'
                        }}>
                          {existing3DFile.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(existing3DFile.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </Box>
                      <IconButton onClick={handleExisting3DFileRemove} disabled={isUploading} color="error">
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  )}

                  {(!existing3DFile || delete3DFile) && (
                    <>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<CloudUploadIcon />}
                        disabled={!!new3DFile || isUploading}
                        sx={{
                          mb: 2,
                          borderRadius: 2,
                          borderColor: alpha(theme.palette.primary.light, 0.38),
                          color: theme.palette.text.primary,
                          '&:hover': {
                            borderColor: alpha(theme.palette.primary.light, 0.6),
                            backgroundColor: alpha(theme.palette.primary.light, 0.12),
                          },
                        }}
                      >
                        3D 파일 선택
                        <input
                          type="file"
                          hidden
                          accept=".3dm,.3ds,.3mf,.amf,.bim,.brep,.dae,.fbx,.fcstd,.gltf,.glb,.ifc,.iges,.igs,.step,.stp,.stl,.obj,.off,.ply,.wrl"
                          onChange={handleNew3DFileSelect}
                        />
                      </Button>

                      {new3DFile && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            border: `1px solid ${alpha(theme.palette.primary.light, 0.4)}`,
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.primary.light, 0.15),
                          }}
                        >
                          <Box sx={{ overflow: 'hidden', width: '100%', flex: 1 }}>
                            <Typography variant="body1" fontWeight="bold" sx={{
                              maxWidth: '100%',
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                              hyphens: 'auto'
                            }}>
                              {new3DFile.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {(new3DFile.size / 1024 / 1024).toFixed(2)} MB (신규 업로드 예정)
                            </Typography>
                          </Box>
                          <IconButton onClick={handleNew3DFileRemove} disabled={isUploading} color="error">
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Box>

              {isUploading && (
                <Box>
                  <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
                    업로드 중... {uploadProgress}%
                  </Typography>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/molds/${id}`)}
                  disabled={saving}
                  sx={{
                    px: 2.5,
                    py: 1,
                    borderRadius: 2,
                    borderColor: alpha(theme.palette.primary.light, 0.38),
                    '&:hover': {
                      borderColor: alpha(theme.palette.primary.light, 0.6),
                      backgroundColor: alpha(theme.palette.primary.light, 0.12),
                    },
                  }}
                >
                  취소
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  startIcon={!saving ? <SaveIcon /> : undefined}
                  sx={{ px: 3.5, py: 1.1, fontWeight: 600, letterSpacing: '0.06em' }}
                >
                  {saving ? <CircularProgress size={20} /> : '저장'}
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MoldEdit;
